import {
  WebsiteDomainStatus,
  type WebsiteDomainRecord,
} from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/** Fewest labels the backend accepts: a subdomain such as www.example.com. */
const MIN_CUSTOM_DOMAIN_LABELS = 3;

const DOMAIN_LABEL_PATTERN = /^[a-z0-9-]+$/;

const isValidDomainLabel = (label: string): boolean =>
  DOMAIN_LABEL_PATTERN.test(label) &&
  !label.startsWith('-') &&
  !label.endsWith('-');

/**
 * Checks a custom domain with the rules the backend applies, so the form can
 * explain a rejected address before it is sent.
 *
 * @param value - Address as typed.
 * @returns The translation key of the problem, or null when the address is
 * valid or still empty.
 */
export const getCustomDomainErrorKey = (
  value: string
): TranslationKey | null => {
  const hostname = value.trim().toLowerCase().replace(/\.$/, '');
  if (!hostname) return null;
  if (/[/:]/.test(hostname)) {
    return 'websites.settings.domains.errors.hostnameOnly';
  }
  if (hostname.startsWith('*.')) {
    return 'websites.settings.domains.errors.wildcard';
  }

  const labels = hostname.split('.').filter(Boolean);
  if (labels.length < MIN_CUSTOM_DOMAIN_LABELS) {
    return 'websites.settings.domains.errors.subdomainRequired';
  }
  return labels.every(isValidDomainLabel)
    ? null
    : 'websites.settings.domains.errors.invalidCharacters';
};

/** What is happening with a domain and what the customer should do next. */
export interface WebsiteDomainHint {
  /** Translation key of the hint. */
  key: TranslationKey;
  /** Original message of an unknown error, kept so support can read it. */
  detail?: string;
}

/**
 * Starts of the English messages the domain verification worker reports,
 * each with the hint shown instead. The timeout message comes first because
 * the worker appends the last pending reason to it.
 */
const DOMAIN_MESSAGE_HINTS: readonly (readonly [string, TranslationKey])[] = [
  [
    'Automatic verification timed out',
    'websites.settings.domains.hints.timedOut',
  ],
  [
    'Certificate validation cannot continue',
    'websites.settings.domains.hints.certificateFailed',
  ],
  [
    'Waiting for certificate validation',
    'websites.settings.domains.hints.waitingCertificate',
  ],
  [
    'CloudFront distribution is still deploying',
    'websites.settings.domains.hints.siteRecordReady',
  ],
  [
    'Hostname is not pointing to the CloudFront distribution',
    'websites.settings.domains.hints.waitingSiteRecord',
  ],
  [
    'Custom domain DNS resolves, but HTTPS',
    'websites.settings.domains.hints.finishingHttps',
  ],
];

/**
 * Hint for a pending domain whose last message is unknown or missing: the
 * record it is waiting for, or the certificate being requested.
 */
const getPendingDomainHintKey = (
  domain: Pick<WebsiteDomainRecord, 'status' | 'verificationRecordName'>
): TranslationKey => {
  if (!domain.verificationRecordName) {
    return 'websites.settings.domains.hints.preparingCertificate';
  }
  return domain.status === WebsiteDomainStatus.PENDING_DNS
    ? 'websites.settings.domains.hints.waitingCertificate'
    : 'websites.settings.domains.hints.waitingSiteRecord';
};

/**
 * Turns a custom domain's state and the worker's last message into a hint in
 * the customer's language. Unknown errors keep the message as a detail.
 *
 * @param domain - Domain status, last message and certificate record.
 * @returns The hint, or null for the provisional domain.
 */
export const getWebsiteDomainHint = (
  domain: Pick<
    WebsiteDomainRecord,
    'status' | 'lastError' | 'verificationRecordName'
  >
): WebsiteDomainHint | null => {
  if (domain.status === WebsiteDomainStatus.PROVISIONAL) return null;
  if (domain.status === WebsiteDomainStatus.ACTIVE) {
    return { key: 'websites.settings.domains.hints.active' };
  }
  // The worker reports "waiting for validation" before ACM has the record,
  // so a hint asking to check the record would point at nothing.
  if (
    domain.status === WebsiteDomainStatus.PENDING_DNS &&
    !domain.verificationRecordName
  ) {
    return { key: 'websites.settings.domains.hints.preparingCertificate' };
  }

  const message = domain.lastError?.trim() ?? '';
  const known = DOMAIN_MESSAGE_HINTS.find(([prefix]) =>
    message.startsWith(prefix)
  );
  if (known) return { key: known[1] };
  if (domain.status === WebsiteDomainStatus.ERROR) {
    return {
      key: 'websites.settings.domains.hints.unknownError',
      detail: message || undefined,
    };
  }
  return { key: getPendingDomainHintKey(domain) };
};
